/**
 * Derives "recently updated" tools from changelog dates.
 * Keep this file free of Bun/Node APIs — it is bundled into the browser.
 */

import type { ChangelogEntry, Tool } from "./types";

export interface RecentTool {
  tool: Tool;
  /** ISO date of the tool's newest dated changelog entry. */
  date: string;
  /** Update notes of that entry, when present. */
  notes?: string;
}

/**
 * The newest dated changelog entry, or undefined when the tool has no
 * parseable dates. Scans all entries rather than trusting newest-first
 * ordering.
 */
export function latestEntry(tool: Tool): ChangelogEntry | undefined {
  let latest: ChangelogEntry | undefined;
  let latestTime = -Infinity;
  for (const entry of tool.changelog ?? []) {
    if (!entry.date) continue;
    const time = Date.parse(entry.date);
    if (Number.isNaN(time) || time <= latestTime) continue;
    latest = entry;
    latestTime = time;
  }
  return latest;
}

/** The newest dated changelog entry's date, or undefined without one. */
export function latestUpdate(tool: Tool): string | undefined {
  return latestEntry(tool)?.date;
}

/** Tools with dated changelogs, newest update first (name breaks ties). */
export function recentlyUpdated(tools: Tool[], limit = 4): RecentTool[] {
  return tools
    .flatMap(tool => {
      const entry = latestEntry(tool);
      if (!entry?.date) return [];
      return [{ tool, date: entry.date, ...(entry.notes ? { notes: entry.notes } : {}) }];
    })
    .sort(
      (a, b) =>
        Date.parse(b.date) - Date.parse(a.date) || a.tool.name.localeCompare(b.tool.name),
    )
    .slice(0, limit);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "2026-06-20" → "Jun 20, 2026". Unpadded parts ("2026-7-8") are accepted.
 * String-based on purpose: Date parsing would shift ISO dates across
 * midnight in west-of-UTC timezones. Non-ISO input is returned unchanged.
 */
export function formatUpdateDate(isoDate: string): string {
  const match = isoDate.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!match) return isoDate;
  const month = MONTHS[Number(match[2]) - 1];
  if (!month) return isoDate;
  return `${month} ${Number(match[3])}, ${match[1]}`;
}
